<script setup>
import {
  ref, computed, watch, onBeforeUnmount,
} from 'vue';
import waveformPeaks from '../../utils/waveform_peaks';

// A sound's waveform, drawn as bars as tall as the volume it plays at. While `playing`, the part
// heard so far fills in, looping for a looping sound.
const BARS = 56;
// The tallest a bar draws, at full volume, as a share of the height; the rest is headroom.
const FULL = 0.92;
// Height of a bar with no sound in it, so the strip never vanishes
const FLOOR = 0.06;

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  volume: {
    type: Number,
    default: 1,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  playing: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['measured']);

const peaks = ref(null);
const duration = ref(0);
// From 0 to 1, how much of the sound has played
const progress = ref(0);
let frame = null;

watch(() => props.src, (src) => {
  peaks.value = null;
  duration.value = 0;
  waveformPeaks(src, BARS).then((shape) => {
    if (src !== props.src) return;
    // Undrawable: a flat strip rather than one that looks forever loading
    peaks.value = shape?.peaks ?? Array(BARS).fill(0);
    duration.value = shape?.duration ?? 0;
    if (shape) emit('measured', shape.duration);
  });
}, { immediate: true });

const reducedMotion = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function stopSweep() {
  cancelAnimationFrame(frame);
  frame = null;
  progress.value = 0;
}

// Reads the length every frame, so a preview started before it was measured sweeps from where it
// is once it is, rather than showing as all played.
watch(() => props.playing, (playing) => {
  stopSweep();
  if (!playing) return;
  if (reducedMotion) {
    progress.value = 1;
    return;
  }
  const start = performance.now();
  const tick = (now) => {
    const played = duration.value ? (now - start) / 1000 / duration.value : 0;
    progress.value = props.loop ? played % 1 : Math.min(1, played);
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
});

onBeforeUnmount(stopSweep);

const bars = computed(() => {
  const shape = peaks.value || Array(BARS).fill(0);
  const scale = props.muted ? 0 : FULL * props.volume;
  return shape.map((peak, i) => {
    const height = Math.max(FLOOR, peak * scale);
    return {
      x: i,
      y: (1 - height) / 2,
      height,
      heard: props.playing && (i + 0.5) / BARS <= progress.value,
    };
  });
});
</script>

<template>
  <svg
    class="h-full w-full"
    :viewBox="`0 0 ${BARS} 1`"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <rect
      v-for="bar in bars"
      :key="bar.x"
      :x="bar.x + 0.2"
      :y="bar.y"
      width="0.6"
      :height="bar.height"
      class="waveform-bar"
      :class="{
        'fill-primary-500': bar.heard,
        'fill-gray-700': muted,
        'fill-gray-500': !bar.heard && !muted,
        'is-loading': !peaks,
      }"
    />
  </svg>
</template>

<style scoped>
.waveform-bar {
  transition: y 180ms ease-out, height 180ms ease-out;
}

.waveform-bar.is-loading {
  opacity: 0.4;
}

@media (prefers-reduced-motion: reduce) {
  .waveform-bar {
    transition: none;
  }
}
</style>
